import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MapPin, Calculator, Gauge, History, Fuel, ArrowRight, Radio, Loader2, AlertTriangle, Star, Power, DollarSign, Wallet, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import useFleetStream from "@/hooks/useFleetStream";
import useSiteConfig from "@/hooks/useSiteConfig";
import BrandLogo from "@/components/BrandLogo";
import DriverAttestationCard from "@/components/DriverAttestationCard";
import DriverNotifications from "@/components/DriverNotifications";
// QuickBook hidden v2.6.6 — import retained as a no-op comment so eslint doesn't flag a future re-enable
// import QuickBook from "@/components/QuickBook";
import { openNavigation } from "@/components/NavigateButton";
import TripNavButtons from "@/components/TripNavButtons";
import PrepaidAccounts from "@/components/PrepaidAccounts";
import MyMetricsCard from "@/components/driver/MyMetricsCard";
import DispatchNotesCard from "@/components/driver/DispatchNotesCard";
import RiderFlagsBadge from "@/components/RiderFlagsBadge";
import EndOfDayDepositCard from "@/components/driver/EndOfDayDepositCard";
import ActiveDeliveryBanner from "@/components/driver/ActiveDeliveryBanner";
import useTelematics from "@/hooks/useTelematics";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";
import CustomerChatWidget from "@/components/customer/CustomerChatWidget";
import { ensureDriverPushSubscription } from "@/lib/driverPush";
import DriverA2HSBanner from "@/components/driver/DriverA2HSBanner";
// v2.7.65+E126 · Driver Console overhaul
import EmergencyActionDrawer from "@/components/driver/EmergencyActionDrawer";
import DriverPreferencesCard from "@/components/driver/DriverPreferencesCard";
import { Zap } from "lucide-react";

// v2.7.0 batch DO+++++ · Milestone B · Telematics opt-in card.
// Surfaces the OS-level DeviceMotion + Geolocation permission prompt and
// persists the choice. Mounted ABOVE MyMetricsCard so drivers see it the
// first time they hit the dashboard. Collapses to a thin "Live tracking · ON"
// status pill once granted.
function TelematicsConsentCard() {
  const { enabled, supported, permission, request, disable } = useTelematics();
  if (!supported) {
    return null;
  }
  if (enabled) {
    return (
      <div data-testid="telematics-active-pill" className="mb-3 px-3 py-1.5 rounded border border-emerald-400/30 bg-emerald-500/5 flex items-center justify-between text-[11px]">
        <span className="text-emerald-300 font-bold">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse"/>
          Safety tracking ON · sensors feeding your driving score
        </span>
        <button
          type="button"
          onClick={disable}
          className="text-zinc-500 hover:text-zinc-300 underline"
          data-testid="telematics-disable"
        >
          turn off
        </button>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded border border-amber-400/30 bg-amber-500/5 p-3" data-testid="telematics-consent-card">
      <div className="flex items-start gap-2">
        <div className="text-amber-300 mt-0.5">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-amber-200">Enable safety tracking?</div>
          <p className="text-[10px] text-amber-100/80 mt-0.5 leading-snug">
            Uses your phone&rsquo;s motion + GPS sensors to compute a live driving score.
            Helps you earn higher rider ratings. Tap below to grant permission.
          </p>
          <button
            type="button"
            onClick={request}
            className="mt-2 text-[10px] px-3 py-1.5 rounded bg-amber-400 text-zinc-950 font-bold hover:bg-amber-300"
            data-testid="telematics-enable"
          >
            Enable Live Score
          </button>
          {permission === "denied" && (
            <div className="text-[9px] text-red-300 mt-1">Permission denied — re-enable from Settings.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [online, setOnline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gpsOk, setGpsOk] = useState(null); // null=unknown, true=ok, false=denied
  const watchRef = useRef(null);
  const pingRef = useRef(0);
  const { cfg } = useSiteConfig();
  const commanderLogo = cfg?.global?.taxi_commander_logo_url;
  // v2.6.9 batch BI · open the real-time WS stream whenever the driver is
  // online. The hook handles geolocation watch + 3 s heartbeat + reconnect.
  const driverJwt = typeof window !== "undefined" ? localStorage.getItem("rtg_token") : null;
  const { status: fleetStatus } = useFleetStream({ role: "driver", token: driverJwt, active: online });

  const refresh = useCallback(() => {
    api.get("/trips/summary").then(r => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { api.get("/drivers/online").then(r => setOnline(!!r.data?.is_online)).catch(()=>{}); }, []);

  const sendPing = useCallback((lat, lng, speed, heading) => {
    api.post("/drivers/location", { lat, lng, speed, heading, on_trip: false }).catch(()=>{});
  }, []);

  const startGpsHeartbeat = useCallback(() => {
    if (!navigator.geolocation) { setGpsOk(false); return; }
    if (watchRef.current != null) return; // already watching
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsOk(true);
        // throttle to one ping every ~25s
        const now = Date.now();
        if (now - pingRef.current < 25000) return;
        pingRef.current = now;
        const { latitude, longitude, speed, heading } = pos.coords;
        sendPing(latitude, longitude, speed || 0, heading || 0);
      },
      () => { setGpsOk(false); },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
    );
  }, [sendPing]);

  const stopGpsHeartbeat = useCallback(() => {
    if (watchRef.current != null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  // Resume the GPS heartbeat on remount when already online
  useEffect(() => { if (online) startGpsHeartbeat(); return stopGpsHeartbeat; }, [online, startGpsHeartbeat, stopGpsHeartbeat]);

  const toggleOnline = async (next) => {
    setBusy(true);
    try {
      await api.post("/drivers/online", { online: next });
      setOnline(next);
      if (next) {
        toast.success("YOU ARE LIVE — accepting bookings");
        startGpsHeartbeat();
        // v2.7.65+E87 · Ensure the driver's browser is subscribed for Web
        // Push notifications so a dispatch Ping (or other urgent alert)
        // can wake the device even when the tab is backgrounded. Silent
        // on Safari-not-PWA — backend falls back to Twilio SMS.
        ensureDriverPushSubscription().then((res) => {
          if (res.subscribed) {
            console.info("[driver-push] subscribed via", res.family);
          } else if (res.reason === "safari_not_pwa") {
            toast.info("Add this app to your Home Screen for background alerts");
          } else if (res.reason?.startsWith("permission_denied")) {
            toast.warning("Notifications blocked — dispatch will still text you.");
          }
        }).catch(() => { /* silent */ });
        // immediately send a one-shot ping so the admin map populates without waiting for the watcher
        // v2.7.65+E75 · Wrap in try/catch — Safari can throw synchronously
        // on getCurrentPosition() when permission is in a transitional
        // "prompt-then-denied" state, and an unhandled rejection here
        // during the online-flip re-render was crashing the Dashboard
        // (black-screen symptom).
        if (navigator.geolocation) {
          try {
            navigator.geolocation.getCurrentPosition(
              (pos) => sendPing(pos.coords.latitude, pos.coords.longitude, 0, 0),
              () => {},
              { enableHighAccuracy: true, timeout: 8000 },
            );
          } catch (_geoErr) { /* soft-fail — the watcher started above will retry */ }
        }
      } else {
        toast.message("You are now OFFLINE");
        stopGpsHeartbeat();
      }
    } catch (e) {
      // v2.7.65+E75 · Detail can be an OBJECT (e.g. HTTP 423 rest_required
      // returns {error, reason, remaining_rest_sec}). Coerce to a
      // human-readable string so sonner doesn't crash trying to render
      // a raw object as the toast body.
      // v2.7.65+E76 · Translate the E72 rest-scenario reason codes
      // (scenario_a_drive_rest_8h · scenario_b_duty_rest_10h ·
      // scenario_c_natural_rest_8h) into a friendly sentence + include
      // the remaining rest window so the driver knows exactly when
      // they can go online.
      const raw = e?.response?.data?.detail;
      const fmtRest = (sec) => {
        const s = Math.max(0, Math.floor(sec || 0));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };
      const scenarioMsg = (reason, remSec) => {
        const rest = remSec ? ` · ${fmtRest(remSec)} remaining` : "";
        if (reason === "scenario_a_drive_rest_8h")
          return `Post-12h drive cap · 8-hour rest required before you can go back online${rest}.`;
        if (reason === "scenario_b_duty_rest_10h")
          return `Post-14h duty cap · 10-hour rest required before you can go back online${rest}.`;
        if (reason === "scenario_c_natural_rest_8h")
          return `Standard 8-hour rest window in progress${rest}. You'll be able to go online once the rest clears.`;
        if (reason === "fresh_shift" || reason === "rest_satisfied") return null;
        return null;
      };
      let msg = "Could not change status";
      if (typeof raw === "string") {
        msg = raw;
      } else if (raw && typeof raw === "object") {
        const translated = scenarioMsg(raw.reason, raw.remaining_rest_sec);
        msg = translated
          || (raw.error === "rest_required"
                ? `DOT rest required · ${fmtRest(raw.remaining_rest_sec)} remaining.`
                : (typeof raw.reason === "string" && raw.reason)
                  || (typeof raw.error === "string" && raw.error)
                  || "Could not change status");
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  // v2.7.65+E148 · Phase 2 · Standardized 6-tile dashboard grid in the
  // spec-mandated 2-column layout. Every tile shares the same aspect
  // ratio + accent icon. Note the TaxiCab Commander rename → "Incident
  // Report" (route unchanged, still lands on /incident-report).
  const tiles = [
    { to: "/new-trip", icon: Zap,            title: "New Trip",         desc: "Upfront quote · flat rates · fuel surcharge",   testid: "tile-new-trip",  accent: "amber" },
    { to: "/queue",    icon: Radio,          title: "Queued Rides",     desc: "Coordinator-assigned rides · start / complete",  testid: "tile-queue",     accent: "amber" },
    { to: "/future-trips", icon: CalendarClock, title: "Future Trips",   desc: "Scheduled advance rides · start when released",  testid: "tile-future-trips", accent: "violet" },
    { to: "/trips",    icon: History,        title: "Trip History",     desc: "All saved trips & summary",                       testid: "tile-trips",     accent: "sky"   },
    { to: "/fleet",    icon: Fuel,           title: "Fleet Options",    desc: "Fuel card · vehicle · oil changes",              testid: "tile-fleet",     accent: "indigo"},
    { to: "/incident-report", icon: AlertTriangle, title: "Incident Report", desc: "File an incident or damage report",         testid: "tile-incident",  accent: "red"   },
    { to: "/end-of-day", icon: Wallet,       title: "End of Day Deposit", desc: "Submit cash / Square / Cash-App / Venmo + tips", testid: "tile-eod",     accent: "emerald" },
  ];

  return (
    <Layout title="Taxicab - Driver" back={false}>
      {/* v2.6.9 batch CH · The previously-centered TAXICOMMANDER brand mark
          at the top of the driver dashboard is now displayed INLINE next to
          "Today's Earnings" (see TodaysEarningsBanner below) per spec. */}
      <TodaysEarningsBanner total={summary?.today?.total} count={summary?.today?.count} commanderLogoUrl={commanderLogo}/>
      {/* v2.7.65+E126 · Driver Console CTA — one-tap jump into the new
          Lyft-style mobile driver surface.
          v2.7.65+E132 · Isolated behind REACT_APP_ENABLE_NEW_DRIVER_UI so the
          new UI doesn't reach production drivers until fully released. */}
      {String(process.env.REACT_APP_ENABLE_NEW_DRIVER_UI || "").toLowerCase() === "true" && (
        <div className="mb-3">
          <Link
            to="/drive"
            data-testid="jump-driver-console"
            className="flex items-center gap-3 rounded-xl border-2 border-fuchsia-400 bg-gradient-to-r from-fuchsia-500/10 to-amber-500/10 px-4 py-3 hover:border-fuchsia-300 active:scale-[0.99] transition-all"
          >
            <div className="h-10 w-10 rounded-full bg-fuchsia-500 grid place-items-center flex-shrink-0">
              <Zap className="h-5 w-5 text-black"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-slate-900 dark:text-zinc-100">Try the new Driver Console</div>
              <div className="text-[11px] text-slate-600 dark:text-zinc-400">
                Full-screen map + safety hub + one-tap navigation deep-links.
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-fuchsia-500 flex-shrink-0"/>
          </Link>
        </div>
      )}
      {/* v2.7.65+E126 · Always-visible dispatch + 911 tap targets. */}
      <div className="mb-3"><EmergencyActionDrawer/></div>
      {/* v2.7.58+E35 · DELIVERY SYSTEM · Section 4 · surface any pending or
          in-progress multi-stop delivery manifests assigned to this driver.
          Pulses amber while awaiting Accept, turns emerald once accepted. */}
      <ActiveDeliveryBanner/>
      <ActiveTripBanner/>
      <QueuedTripBanner/>
      <AssignedQueueBanner/>
      {/* v2.7.0 batch DO+++++ · Milestone B · Driver "My Metrics" performance
          card with live charts + telematics-driven driving score. */}
      <MyMetricsCard/>
      <TelematicsConsentCard/>
      {/* v2.7.65+E126 · Notification preference toggles (SMS / Voice / Push)
          with localStorage cache so it doesn't re-fetch on every visit. */}
      <DriverPreferencesCard/>
      {/* v2.7.56+E33 · End-of-Day Deposit card — driver submits their shift
          breakdown (cash / Square / Cash-App / Venmo / tips). Auto-sums the
          totals live; receipt upload is optional. */}
      <EndOfDayDepositCard/>
      {/* v2.6.8 batch O · in-app notifications from dispatch (e.g. missing
          TAXIcab Permit reminders) — banner collapses when inbox is empty. */}
      <DriverNotifications/>
      {/* v2.6.8 batch N · attestation card at the top of the driver dashboard
          so OSBI / OKDMV / TAXIcab PERMIT info is one tap away on every login. */}
      <DriverAttestationHomeCard/>
      {/* v2.6.9 batch BI · live GPS stream status pill. Drivers see this when
          they're online so they know dispatch is receiving their 3 s pings.
          When the WS drops (tab backgrounded / network blip), the pill turns
          amber + audibly toasts so the driver re-foregrounds the tab. */}
      {online && (
        <>
          <BackgroundMeterPermissionCard/>
          <Card className="bg-zinc-900 border-zinc-800 p-3 mb-3" data-testid="fleet-stream-pill">
          <div className="flex items-center gap-2 text-xs">
            <span className="relative inline-flex h-2.5 w-2.5 flex-shrink-0">
              {fleetStatus === "open" && <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping"/>}
              <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${fleetStatus === "open" ? "bg-green-400" : fleetStatus === "connecting" ? "bg-amber-400" : "bg-rose-400"}`}/>
            </span>
            <span className="font-bold uppercase tracking-wider">
              {fleetStatus === "open" ? "GPS Streaming · Dispatch sees you" : fleetStatus === "connecting" ? "Connecting…" : "Stream offline — keep this tab open"}
            </span>
            <span className="text-zinc-500 text-[10px]">· 3-sec heartbeat</span>
          </div>
        </Card>
        </>
      )}
      {/* v2.6.9 batch BI-2 · OFFICIAL DRIVER GO ONLINE TOGGLE.
          Flipping this to ON: (1) hits /drivers/online so dispatch knows
          you're available, (2) starts the 25 s legacy HTTP heartbeat, (3)
          activates the real-time `useFleetStream` WebSocket so the Admin
          Live Map sees your taxi marker every 3 s. Flipping OFF stops all
          three immediately and removes your marker after the 10 s
          signal-lost window. This IS the official "go-live" sequence. */}
      <Card className={`p-5 mb-3 border-2 ${online ? "bg-green-500/10 border-green-400/50" : "bg-zinc-900 border-zinc-800"}`} data-testid="driver-go-online-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${online ? "bg-green-400 text-zinc-950" : "bg-zinc-800 text-zinc-500"}`}>
              <Power className="h-6 w-6"/>
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest font-bold text-zinc-400">Driver Status</div>
              <div className={`text-xl font-extrabold ${online ? "text-green-300" : "text-zinc-200"}`} data-testid="driver-status-label">
                {online ? "● ONLINE · Streaming live GPS" : "OFFLINE"}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {online ? "Dispatch sees your live position on the map · 3-sec heartbeat" : "Go online to receive bookings and start GPS streaming to dispatch."}
              </div>
            </div>
          </div>
          <Button
            data-testid="driver-go-online-btn"
            disabled={busy}
            onClick={() => toggleOnline(!online)}
            className={`h-14 px-6 font-extrabold tracking-widest text-base ${online ? "bg-rose-500 hover:bg-rose-400 text-zinc-950" : "bg-green-500 hover:bg-green-400 text-zinc-950"}`}
          >
            {busy
              ? <Loader2 className="h-5 w-5 animate-spin"/>
              : (online ? <><Power className="h-5 w-5 mr-2"/>GO OFFLINE</> : <><Power className="h-5 w-5 mr-2"/>GO ONLINE</>)}
          </Button>
        </div>
        {online && gpsOk === false && (
          <div className="mt-3 p-2 rounded bg-rose-500/10 border border-rose-400/40 text-xs text-rose-200 flex items-center gap-2" data-testid="driver-gps-blocked">
            <AlertTriangle className="h-4 w-4 flex-shrink-0"/>
            GPS permission was denied — enable Location Services for this browser so dispatch can see you.
          </div>
        )}
      </Card>

      {/* v2.8+E314 · DOT Hours-of-Service compliance panel removed — the DOT
          Compliance System is disabled platform-wide. */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="earnings-summary">
        <SummaryCard label="Today" total={summary?.today?.total} count={summary?.today?.count}/>
        <SummaryCard label="This Week" total={summary?.week?.total} count={summary?.week?.count}/>
        <SummaryCard label="All Time" total={summary?.all_time?.total} count={summary?.all_time?.count}/>
      </div>

      {/* v2.6.9 batch BB-2 · Tip Pool — rolled up from per-trip tip_amount captured at close-out. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="tips-summary">
        <TipCard label="Tips Today" total={summary?.today?.tips}/>
        <TipCard label="Tips This Week" total={summary?.week?.tips}/>
        <TipCard label="Tips All Time" total={summary?.all_time?.tips}/>
      </div>

      {/* v2.7.0 batch DM · Pre-Paid Accounts queue. Only shown when online — a
          driver offline can't run a meter so we hide it to reduce clutter. */}
      {online && (
        <div className="mb-6">
          <PrepaidAccounts onTripCompleted={() => { refresh(); }}/>
        </div>
      )}

      {/* QuickBook · Auto Fare hidden per user request v2.6.6 — drivers use
         Live Meter or Custom Trip exclusively for quoting. The component file
         is kept so the legacy /quickbook route still resolves; can be deleted
         later if you decide it's truly gone for good. */}
      {/* <QuickBook onSave={refresh} /> */}

      {/* v2.7.65+E148 · Phase 2 · Standardized 2-column tile grid.
          Every tile shares a 1:1 aspect ratio with centered icon +
          label so the dashboard stays visually calm regardless of how
          long the tile description reads. Six tiles fit in a clean
          3×2 layout on tablet/desktop and 6×1 on mobile. */}
      <div className="driver-dashboard-grid grid grid-cols-2 gap-4 p-1" data-testid="driver-dashboard-grid">
        {tiles.map(t => {
          const accent = t.accent || "amber";
          const isEmerald = accent === "emerald";
          const ACCENT = {
            amber:   "bg-amber-400/10 border-amber-400/30 text-amber-400 group-hover:bg-amber-400 group-hover:text-zinc-950",
            sky:     "bg-sky-400/10 border-sky-400/30 text-sky-300 group-hover:bg-sky-400 group-hover:text-zinc-950",
            indigo:  "bg-indigo-400/10 border-indigo-400/30 text-indigo-300 group-hover:bg-indigo-400 group-hover:text-zinc-950",
            red:     "bg-red-400/10 border-red-400/30 text-red-300 group-hover:bg-red-400 group-hover:text-zinc-950",
            emerald: "bg-emerald-400/10 border-emerald-400/40 text-emerald-300 group-hover:bg-emerald-400 group-hover:text-zinc-950",
          }[accent];
          return (
            <Link key={t.to} to={t.to} data-testid={t.testid} className="block h-full">
              <Card className={`driver-tile-card group bg-zinc-900 hover:bg-zinc-800 border-zinc-800 transition-all cursor-pointer aspect-square flex flex-col items-center justify-center text-center p-4 ${
                isEmerald ? "hover:border-emerald-400/60 ring-1 ring-emerald-400/20"
                          : "hover:border-amber-400/40"
              }`}>
                <div className={`h-14 w-14 rounded-xl border flex items-center justify-center transition-colors ${ACCENT}`}>
                  <t.icon className="h-7 w-7"/>
                </div>
                <h3 className="text-base sm:text-lg font-bold mt-3 leading-tight">{t.title}</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug line-clamp-2">{t.desc}</p>
              </Card>
            </Link>
          );
        })}
      </div>
      <CustomerChatWidget/>
      <DriverA2HSBanner/>
    </Layout>
  );
}

// v2.6.9 batch BL · "Today's Earnings" hero banner — pinned at the top of
// the Driver Dashboard. Small curved-edge rectangle (BG #1A1A1A, bold yellow
// text), font auto-scales based on digit count so $0.00 → $9,999.99 all fit.
function TodaysEarningsBanner({ total, count, commanderLogoUrl }) {
  const amt = Number(total || 0);
  const display = `$${amt.toFixed(2)}`;
  // Auto-scale: shrink as the digit count grows so $0.00 looks huge and
  // $99,999.99 still fits comfortably on small viewports.
  const len = display.length;
  const fontSize = len <= 6 ? "text-5xl sm:text-6xl"
    : len <= 8 ? "text-4xl sm:text-5xl"
    : len <= 10 ? "text-3xl sm:text-4xl"
    : "text-2xl sm:text-3xl";
  return (
    <Link to="/earnings" data-testid="todays-earnings-banner" className="block mb-3">
      {/* v2.7.0 batch DO+++++++ · Driver hero with the same amber neon-glow
          treatment as the Admin Earnings hero — pulse animation modulates
          only the text-shadow, never the figure itself. */}
      <div className="hero-revenue-card glow-amber px-5 py-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {/* v2.6.9 batch CH · TAXICOMMANDER logo placed directly to the
                LEFT of "Today's Earnings". Scaled to xs (~48 px) — about
                50% of the original md (96 px) brand-mark size. Hides when
                the CMS URL is blank. */}
            {commanderLogoUrl ? (
              <BrandLogo src={commanderLogoUrl} alt="TAXIcommander" size="xs" className="flex-shrink-0" testid="todays-earnings-brand"/>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300 flex-shrink-0">
                <DollarSign className="h-5 w-5"/>
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Today's Earnings</div>
              <div className={`hero-revenue-figure meter-display ${fontSize} font-extrabold leading-none mt-1 text-amber-300`} data-testid="todays-earnings-amount">
                {display}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">{count || 0} trip{count === 1 ? "" : "s"} · tap for full breakdown</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-amber-300/80 text-[10px] uppercase tracking-widest font-bold">
            View Earnings <ArrowRight className="h-3.5 w-3.5"/>
          </div>
        </div>
      </div>
    </Link>
  );
}


function SummaryCard({ label, total, count }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="meter-display text-3xl font-bold mt-1 text-amber-400">{fmtUsd(total)}</div>
      <div className="text-xs text-zinc-400 mt-1">{count ?? 0} trips</div>
    </Card>
  );
}

// v2.6.9 batch BB-2 · Tip Pool card. Renders flat-zero state when the driver
// hasn't received any tips yet so the driver sees the section exists.
function TipCard({ label, total }) {
  const amt = Number(total || 0);
  return (
    <Card className="bg-zinc-900 border-emerald-500/30 p-4" data-testid={`tip-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-emerald-400">{label}</div>
        <Star className="h-3.5 w-3.5 text-emerald-400 fill-emerald-400"/>
      </div>
      <div className="meter-display text-3xl font-bold mt-1 text-emerald-400">{fmtUsd(amt)}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{amt > 0 ? "Captured at trip close-out" : "No tips yet — add tip at close-out"}</div>
    </Card>
  );
}

function ActiveTripBanner() {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [busy, setBusy] = useState(false);
  const watchRef = useRef(null);
  const lastPingRef = useRef(0);

  const reload = useCallback(() => {
    api.get("/drivers/active-trip").then(r => setTrip(r.data?.ride || null)).catch(()=>{});
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 5000);
    const onAccepted = () => reload();
    window.addEventListener("ride-accepted", onAccepted);
    return () => { clearInterval(t); window.removeEventListener("ride-accepted", onAccepted); };
  }, [reload]);

  // Live GPS ping while ride.status === "in_progress" — every 10s.
  useEffect(() => {
    const stop = () => {
      if (watchRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };
    if (!trip || trip.status !== "in_progress" || !navigator.geolocation) {
      stop();
      return;
    }
    const rideId = trip.id;
    if (watchRef.current != null) return;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastPingRef.current < 10000) return;
        lastPingRef.current = now;
        const { latitude, longitude, speed, heading } = pos.coords;
        api.post(`/rides/${rideId}/ping`, { lat: latitude, lng: longitude, speed: speed || 0, heading: heading || 0 }).catch(()=>{});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );
    return stop;
  }, [trip?.id, trip?.status]);

  const start = async () => {
    // Step 1 · open native maps to the PICKUP address, fired synchronously
    // on the click (before any await) so mobile popup-blockers don't eat it.
    // The Live Meter hand-off below is unchanged — the meter still launches
    // drop-off navigation on its own Start, so the two steps stay sequential.
    if (trip.origin) openNavigation(trip.origin);
    setBusy(true);
    try {
      await api.post(`/rides/${trip.id}/start`);
      toast.success("Trip started · navigating to pickup");
      // v2.6.9 batch CQ · After starting, open the matching meter variant
      // pre-loaded with the trip's rider info + origin/destination so the
      // driver never has to retype anything. `meter_hint` from the ride
      // doc routes statewide trips to /meter-statewide, city otherwise.
      const params = new URLSearchParams();
      params.set("trip_id", trip.id);
      if (trip.origin)         params.set("origin", trip.origin);
      if (trip.destination)    params.set("destination", trip.destination);
      if (trip.passengers)     params.set("passengers", String(trip.passengers));
      if (trip.quoted_fare)    params.set("quote_total", String(trip.quoted_fare));
      if (trip.customer_name)  params.set("customer_name", trip.customer_name);
      if (trip.customer_phone) params.set("customer_phone", trip.customer_phone);
      if (trip.customer_email) params.set("customer_email", trip.customer_email);
      const path = trip.meter_hint === "statewide" ? "/meter-statewide" : "/meter";
      navigate(`${path}?${params.toString()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };
  const end = async () => {
    if (!window.confirm(`End ${trip.waybill_no}?`)) return;
    setBusy(true);
    try { await api.post(`/rides/${trip.id}/end`, {}); toast.success("Trip completed · marked DONE"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not end"); }
    finally { setBusy(false); }
  };
  // v2.8.2 · Close/dismiss a stuck or unresolvable trip WITHOUT recording it as
  // a completed trip (does not touch the Completed Rides ledger).
  const dismiss = async () => {
    if (!window.confirm("Close this trip and remove it from your app?\n\nUse this only for a stuck, duplicate, or old trip. It will NOT be recorded as a completed trip.")) return;
    setBusy(true);
    try {
      await api.post(`/rides/${trip.id}/driver-dismiss`);
      toast.success("Trip closed & cleared from your app");
      setTrip(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close trip"); }
    finally { setBusy(false); }
  };

  if (!trip) return null;

  return (
    <Card className="bg-amber-400/10 border-amber-400 p-5 mb-4 fade-in" data-testid="active-trip-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold flex items-center gap-1.5">
            Active Trip · {trip.status?.toUpperCase().replace("_", " ")}
            {trip.status === "in_progress" && (
              <span className="inline-flex items-center gap-1 text-green-400">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 pulse-dot"/>LIVE GPS
              </span>
            )}
          </div>
          <div className="text-sm font-semibold mt-1 truncate">{trip.waybill_no} · {trip.origin} → {trip.destination}</div>
          <div className="text-[11px] text-zinc-400">{trip.business_name || trip.customer_name} · {trip.passengers} pax · {trip.payment_method?.toUpperCase()} · {fmtUsd(trip.total)}</div>
          {trip.client_status && trip.client_status !== "normal" && (
            <div className="text-[10px] text-red-400 font-bold uppercase mt-1">⚠ {trip.client_status.replace("_", " ")}</div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <TripNavButtons pickup={trip.origin} dropoff={trip.destination} testidPrefix="trip-nav"/>
          {trip.status === "accepted" && (
            <Button data-testid="trip-start-btn" onClick={start} disabled={busy} className="bg-green-500 hover:bg-green-400 text-zinc-950 font-bold">START TRIP</Button>
          )}
          {(trip.status === "accepted" || trip.status === "in_progress") && (
            <Button data-testid="trip-end-btn" onClick={end} disabled={busy} variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-400 font-bold">END · MARK DONE</Button>
          )}
          {/* v2.8.2 · Always-available escape hatch for stuck/old trips. */}
          <Button data-testid="trip-dismiss-btn" onClick={dismiss} disabled={busy} variant="outline" className="bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-500/10 font-bold text-xs">Close Trip</Button>
        </div>
      </div>
    </Card>
  );
}



function QueuedTripBanner() {
  const [queue, setQueue] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const reload = useCallback(() => {
    api.get("/drivers/queue").then(r => setQueue(r.data?.queue || [])).catch(()=>{});
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 8000); return () => clearInterval(t); }, [reload]);
  const promote = async (qid) => {
    try { await api.post(`/drivers/queue/${qid}/promote`); toast.success("Queued trip promoted to active"); window.dispatchEvent(new Event("ride-accepted")); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };
  const release = async (qid, waybill) => {
    if (!window.confirm(`Release queued trip ${waybill || ""}? Dispatch will reassign it.`)) return;
    try { await api.post(`/drivers/queue/${qid}/release`); toast.message("Released back to dispatch"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
  };
  if (queue.length === 0) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="queued-trip-banner">
      <div className="flex items-center justify-between bg-blue-500/10 border border-blue-400/40 rounded-md px-3 py-2">
        <div className="flex items-center gap-2 text-blue-200">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Queued by dispatch</span>
          <span className="bg-blue-400 text-zinc-950 text-xs font-extrabold rounded-full px-2 py-0.5" data-testid="queue-count-badge">{queue.length}</span>
        </div>
        <button
          data-testid="queue-toggle"
          onClick={() => setCollapsed(c => !c)}
          className="text-[10px] uppercase tracking-wider text-blue-200 hover:text-blue-100"
        >
          {collapsed ? "Show" : "Hide"} queue
        </button>
      </div>
      {!collapsed && queue.map(q => (
        <Card key={q.id} className="bg-blue-500/10 border-blue-400/40 p-4 fade-in" data-testid={`queue-item-${q.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300 font-bold">Next trip queued by dispatch</div>
              <div className="text-sm font-semibold mt-1 truncate">{q.ride?.waybill_no} · {q.ride?.origin} → {q.ride?.destination}</div>
              <div className="text-[11px] text-zinc-400">{q.ride?.business_name || q.ride?.customer_name} · {q.ride?.passengers} pax · {fmtUsd(q.ride?.total)}</div>
              {q.note && <div className="text-[10px] text-amber-400 mt-0.5">⚠ {q.note}</div>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button size="sm" onClick={() => promote(q.id)} className="bg-blue-500 hover:bg-blue-400 text-zinc-950 font-bold" data-testid={`queue-promote-${q.id}`}>START NOW</Button>
              <Button size="sm" variant="outline" onClick={() => release(q.id, q.ride?.waybill_no)} className="bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-500/10 text-[10px]" data-testid={`queue-release-${q.id}`}>RELEASE</Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// v2.8.1 · Bug fix — Book Ride / Ride-Coordinator assignments land in the
// `queued_trips` system (surfaced on the /queue page). Drivers were missing
// them on their home screen, so this live banner surfaces those assigned
// rides right on the dashboard with a one-tap jump to start them. Reads the
// SAME endpoint the /queue page uses and polls every 8s so a freshly-assigned
// ride appears without a manual refresh.
function AssignedQueueBanner() {
  const [queue, setQueue] = useState([]);
  const navigate = useNavigate();
  const reload = useCallback(() => {
    api.get("/drivers/queued-trips")
      .then(r => setQueue((r.data?.queue || []).filter(q => q.state === "queued" || q.state === "in_progress")))
      .catch(() => {});
  }, []);
  useEffect(() => {
    reload();
    const t = setInterval(reload, 8000);
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    window.addEventListener("rtg:new_trip_created", reload);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("rtg:new_trip_created", reload);
    };
  }, [reload]);
  if (queue.length === 0) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="assigned-queue-banner">
      <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-400/40 rounded-md px-3 py-2 text-amber-200">
        <Radio className="h-3.5 w-3.5"/>
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Assigned by dispatch</span>
        <span className="bg-amber-400 text-zinc-950 text-xs font-extrabold rounded-full px-2 py-0.5" data-testid="assigned-queue-count">{queue.length}</span>
      </div>
      {queue.map(q => {
        const started = q.state === "in_progress";
        return (
          <Card key={q.id} className={`p-4 fade-in ${q.rider_flags?.length ? "border-2 border-red-500 bg-red-500/5" : started ? "border-emerald-400/60 bg-emerald-500/5" : "border-amber-400/50 bg-amber-500/5"}`} data-testid={`assigned-queue-item-${q.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${started ? "bg-emerald-500/20 text-emerald-400 border-emerald-400/40" : "bg-amber-500/20 text-amber-500 border-amber-400/40"}`}>
                    {started ? "IN PROGRESS" : "QUEUED"}
                  </span>
                  <span className="font-bold text-zinc-100 truncate">{q.rider_name || "Rider"}</span>
                  {q.payment_status === "paid" && <span className="text-[9px] uppercase font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-400/40">PAID</span>}
                </div>
                <div className="text-xs text-zinc-300 mt-1 flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 text-emerald-400 flex-shrink-0"/><span className="min-w-0"><b className="text-zinc-400">Pickup:</b> {q.pickup_address || "—"}</span></div>
                <div className="text-xs text-zinc-300 flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 text-amber-400 flex-shrink-0"/><span className="min-w-0"><b className="text-zinc-400">Drop-off:</b> {q.dropoff_address || q.dropoff_label || "—"}</span></div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{q.passengers || 1} pax · {fmtUsd(q.total)}</div>
              </div>
              <Button size="sm" onClick={() => navigate("/queue")} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid={`assigned-queue-start-${q.id}`}>
                {started ? "OPEN NAV" : "START TRIP"}<ArrowRight className="h-3.5 w-3.5 ml-1"/>
              </Button>
            </div>
            {/* v2.8+E319 · Rider flags — prominent on the home-screen ride card. */}
            {q.rider_flags?.length > 0 && (
              <div className="mt-2">
                <RiderFlagsBadge flags={q.rider_flags} testId={`assigned-queue-item-${q.id}-rider-flags`}/>
              </div>
            )}
            {/* v2.8+E315 · Dispatch trip notes surfaced on the home-screen ride
                card too, so the driver sees them the moment a ride is assigned. */}
            <div className="mt-2">
              <DispatchNotesCard note={q.dispatch_notes} testId={`assigned-queue-item-${q.id}-dispatch-notes`} onFirstOpen={() => api.post(`/drivers/queued-trips/${q.id}/notes-viewed`).catch(() => {})} />
            </div>
          </Card>
        );
      })}
    </div>
  );
}


// v2.6.8 batch N · fetches the driver's own profile + renders the shared
// attestation card on the Dashboard home so it's always one tap away.
function DriverAttestationHomeCard() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then(r => setMe(r.data)).catch(()=>{}); }, []);
  if (!me) return null;
  return <div className="mb-4"><DriverAttestationCard driver={me} variant="self"/></div>;
}


// ============================================================================
// v2.7.0 batch DK · Background-Meter permission card
// ----------------------------------------------------------------------------
// Asks drivers (once) to grant the Notification permission. With it granted,
// the useFleetStream hook posts a persistent "Meter running" notification
// while a trip is active — this dramatically lowers the chance Android (or
// foreground-aggressive iOS) suspends the meter tab when the driver swipes
// over to Google Maps / Waze for turn-by-turn navigation.
//
// The card auto-hides once permission is granted/denied so it doesn't nag.
// Dismissed state is stored in localStorage so a driver who hits "Not now"
// won't see it again for 7 days.
// ============================================================================
function BackgroundMeterPermissionCard() {
  const [perm, setPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "granted"
  );
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("rtg_bgmeter_dismissed_at");
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) setDismissed(true);
      }
    } catch { /* localStorage may be blocked in private mode */ }
  }, []);

  if (typeof Notification === "undefined") return null;     // browser doesn't support
  if (perm === "granted" || perm === "denied") return null; // already settled
  if (dismissed) return null;

  const enable = async () => {
    try {
      const next = await Notification.requestPermission();
      setPerm(next);
      if (next === "granted") {
        toast.success("Background meter alerts enabled — your meter will stay alive across app switches.");
      }
    } catch (e) {
      console.warn("[bgmeter] permission request failed:", e?.message || e);
    }
  };
  const later = () => {
    try { localStorage.setItem("rtg_bgmeter_dismissed_at", String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <Card className="bg-amber-950/20 border-amber-400/50 p-3 mb-3" data-testid="bgmeter-permission-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-amber-400 text-zinc-950 flex items-center justify-center flex-shrink-0">
          <Radio className="h-4 w-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">Keep your meter alive in the background</div>
          <p className="text-xs text-amber-100 mt-1 leading-snug">
            Allow notifications and we&apos;ll post a persistent &quot;Meter running&quot; alert during every trip.
            Android keeps your tab high-priority, so switching to Maps or Waze won&apos;t kill the meter.
            Tap the notification any time to jump back to the meter.
          </p>
          <div className="flex gap-2 mt-2">
            <Button onClick={enable} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-8 text-xs" data-testid="bgmeter-enable">
              Enable background alerts
            </Button>
            <Button onClick={later} variant="ghost" className="text-amber-200 hover:bg-amber-900/20 h-8 text-xs" data-testid="bgmeter-later">
              Not now
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
